import React, { useContext, useEffect, useState } from 'react'
import _ from 'lodash'
import { jsPDF } from "jspdf";
import { useParams, useNavigate } from "react-router-dom"
import { Button, Form, Input, Spin, SelectProps, Select, InputNumber, App, Upload, TreeSelect, Switch, Divider, TimePicker, Space, Typography } from "antd"
import { DatePicker } from "antd-jalali"
import dayjs, { Dayjs } from 'dayjs'
import { FaPlus, FaPrint, FaRegSave, FaInbox } from "react-icons/fa"
import { API, Enum, Regex, Font } from "../../../resources/constants"
import language from "../../../resources/languages/fa_IR"

// Contexts
import GlobalContext from "../../../contexts/global";
import AxiosContext from "../../../contexts/axios";

// Interfaces
import { AxiosError } from "../../../interfaces/axios"

const { Pages: { TicketManagement: { Price: PriceLanguage } }, Enum: EnumLanguage, Share, Validation, Message: MessageLanguage } = language
const { BASIC_INFORMATION_PROVINCE_CITIES, TICKET_MANAGEMENT_PRICE_ID, TICKET_MANAGEMENT_PRICE_CREATE, TICKET_MANAGEMENT_PRICE_UPDATE } = API
const { Message, Payment } = Enum

interface ProvincesType {
    id: string,
    name: string,
    cities: CityType[],
}

interface ProvinceType {
    id: string,
    name: string,
    publish: boolean,
    createdAt: Date,
    updatedAt: Date,
}

interface CityType {
    id: string,
    name: string,
    province: ProvinceType,
    publish: boolean,
    createdAt: Date,
    updatedAt: Date,
}

interface PriceType {
    row: number,
    id: string,
    key: string,
    destination : CityType,
    price: number,
    createdAt: Date,
    updatedAt: Date,
}

interface PriceFormType {
    destination: string,
    price: number,
}

export default function CURecord() {

    const [form] = Form.useForm()
    const [, forceUpdate] = useState({})
    const navigate = useNavigate()
    const { accessToken, baseURL, meta } = useContext(GlobalContext)
    const { axios } = useContext(AxiosContext)
    const [loadingProvinces, setLoadingProvinces] = useState<boolean>(true)
    const [provinces, setProvinces] = useState<ProvincesType[]>([])
    const [loadingRecord, setLoadingRecord] = useState<boolean>(false)
    const { id: priceId } = useParams()
    const { message } = App.useApp()

    useEffect(() => {
        // To disable submit button at the beginning.
        forceUpdate({})
        initialAPI().then(response => response)
    }, [])

    const initialAPI = async () => {
        await getProvinces()
        if (priceId) {
            setLoadingRecord(true);
            await getRecord(priceId)
        }
    }

    const getProvinces = async () => axios.get(BASIC_INFORMATION_PROVINCE_CITIES).then((response: { data: ProvincesType[] }) => {
        const { data: provinces } = response
        setProvinces(provinces)
        setLoadingProvinces(false)
    }).catch((error: AxiosError) => {
        const { response: { data } } = error;
        // @ts-ignore
        message.error(MessageLanguage[data.message]).then(response => response )
    })

    const getRecord = async (priceId: string): Promise<PriceType> => axios.get(TICKET_MANAGEMENT_PRICE_ID + priceId).then(async (response: { data: PriceType }) => {
        const { data: price } = response
        form.setFieldsValue({ ...price, destination: price.destination.id })
        setLoadingRecord(false)
        return price;
    }).catch((error: AxiosError) => {
        const { response: { data } } = error;
        // @ts-ignore
        message.error(MessageLanguage[data.message]).then(response => response )
    })

    const onFinish = async (price: PriceFormType) => {
        setLoadingRecord(true);

        if (priceId) {
            return axios.patch(TICKET_MANAGEMENT_PRICE_UPDATE + priceId, price).then(async (response: {}) => {
                message.success(MessageLanguage[Message.UPDATE_RECORD]).then(response => response)
                await getRecord(priceId)
            }).catch((error: AxiosError) => {
                const { response: { data } } = error;
                // @ts-ignore
                message.error(MessageLanguage[data.message]).then(response => response)
                setLoadingRecord(false)
            })
        } else {
            return axios.post(TICKET_MANAGEMENT_PRICE_CREATE, price).then(async (response: { data: { id: string } }) => {
                message.success(MessageLanguage[Message.CREATE_RECORD]).then(response => response)
                const { data: { id } } = response
                navigate('/ticket-management/price/edit/' + id)
                await getRecord(id)
            }).catch((error: AxiosError) => {
                const { response: { data } } = error;
                // @ts-ignore
                message.error(MessageLanguage[data.message]).then(response => response)
                setLoadingRecord(false)
            })
        }
    }

    return (
        <div id={'price'}>
            <Spin spinning={loadingRecord || loadingProvinces} tip={Share.loading} size="large">
                <Form form={form} name="cu-record" onFinish={onFinish} style={{ maxWidth: 450 }}>
                    <Form.Item
                        label={PriceLanguage.destination}
                        name="destination"
                        hasFeedback
                        rules={[
                            { required: true, message: `${PriceLanguage.destination} ${Validation.required}` },
                        ]}
                    >
                        <Select
                            showSearch
                            allowClear
                            placeholder={Share.pleaseSelectedRecords}
                            optionFilterProp="name"
                            options={provinces.map(({ name, cities }) => ({ label: name, options: cities.map(({ id, name }) => ({ label: name, value: id, name }))}))}
                        />
                    </Form.Item>
                    <Form.Item
                        label={PriceLanguage.price}
                        name="price"
                        hasFeedback
                        rules={[
                            { required: true, message: `${PriceLanguage.price} ${Validation.required}` },
                        ]}
                    >
                        <InputNumber
                            style={{width: '100%'}}
                            addonAfter={Share.rial}
                            formatter={(value: any) => Math.trunc(Math.abs(value)).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                        />
                    </Form.Item>
                    <Form.Item shouldUpdate>
                        {() => (
                            <Button
                                icon={<FaRegSave />}
                                type="primary"
                                htmlType="submit"
                                block
                                disabled={
                                    !form.getFieldValue('destination') ||
                                    !form.getFieldValue('price') ||
                                    !!form.getFieldsError().filter(({ errors }) => errors.length).length
                                }
                            >
                                { Share.save }
                            </Button>
                        )}
                    </Form.Item>
                </Form>
            </Spin>
        </div>
    )
}
